/**
 * Browser log backend that POSTs to this app's own `/api/log` Worker route.
 *
 * The browser never talks to the sink directly: an Axiom write token belongs
 * to a backend, never to a browser bundle. The Worker route
 * (apps/retells/worker/api-log.ts) holds the token as a Cloudflare Worker
 * secret and forwards validated events server-side.
 *
 * This POSTs `toAxiomEvent(entry)` — the canonical wide-event shape —
 * wrapped in a one-element array, matching the route's `Array.isArray(body)`
 * batch contract (batches of any size >= 1). No client-side batching: every
 * `write()` call is its own POST, immediately, with `keepalive: true` so the
 * request survives `pagehide` / unload (logging-strategy POLICY.md §8). Real
 * batching (queue + debounce + `pagehide` flush) is a deliberate scope cut.
 *
 * ## The keepalive quota is a shared, per-document budget
 *
 * Per the Fetch spec, the summed body size of every in-flight keepalive
 * request in a document is capped at 64 KiB, and a request that would push
 * the sum past it **fails with a network error rather than queueing behind
 * the others**. Per-write POSTing spends that quota one event at a time at
 * exactly the moment events cluster — a page hide flushes LCP, INP and CLS
 * together, plus a long-task chunk and any pressure transitions.
 *
 * There is no retry here and the route's response is never read, so a
 * rejected POST leaves no trace on the client at all; it is simply an event
 * that never existed. That is why this backend budgets instead of hoping:
 *
 * - An event whose body exceeds `MAX_KEEPALIVE_EVENT_BYTES` is sent without
 *   `keepalive`. It still goes out, and during the page's life that is an
 *   identical request; it forfeits only the unload case, which is the right
 *   trade against letting one fat `data` bag evict the small vitals events
 *   around it.
 * - The bytes this backend currently has in flight are tracked, and a write
 *   that would carry the sum past `KEEPALIVE_QUOTA_BYTES` degrades the same
 *   way rather than being rejected outright. The count is a lower bound —
 *   anything else on the page issuing keepalive fetches, or a
 *   `navigator.sendBeacon` call, draws on the same pool unseen — which is
 *   why the per-event budget is sized well under a strict quota split rather
 *   than relying on this accounting alone.
 *
 * Degrading is a deviation from policy §8's "one path, always keepalive"
 * only in letter: §8 exists so events survive unload, and a request the
 * browser refuses to issue survives nothing.
 */

import { serializeEvents, toAxiomEvent } from "../axiom-event";
import {
  byteLength,
  KEEPALIVE_QUOTA_BYTES,
  MAX_KEEPALIVE_EVENT_BYTES,
} from "../ingest-limits";
import type { BaseLogEntry } from "../types";
import type { LogBackend } from "./interface";
import { logDevError } from "./log-dev-error";

export class WorkerLogBackend implements LogBackend {
  private readonly endpoint: string;

  /**
   * Bytes of this backend's own keepalive requests that have not settled yet.
   * Per instance rather than per module: two backends pointed at different
   * endpoints are two independent transports. The spec's quota is the
   * document's, though, so a page should build ONE instance per endpoint and
   * share it across its Loggers — each extra instance counts only its own
   * requests against the whole 64 KiB (apps/retells/src/logging/
   * pageBackend.ts is how retells does it).
   */
  private inFlightKeepaliveBytes = 0;

  constructor(endpoint = "/api/log") {
    this.endpoint = endpoint;
  }

  write(entry: BaseLogEntry): void {
    // serializeEvents does not throw: a `data` bag that will not serialize
    // costs the event its `data`, not the event itself, and the byte count
    // below is taken over what is actually sent.
    const body = serializeEvents([toAxiomEvent(entry)]);
    const bytes = byteLength(body);
    const keepalive = this.reserve(bytes);

    try {
      void fetch(this.endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive,
      })
        .catch((err: unknown) => {
          logDevError("WorkerLogBackend", "fetch", err);
        })
        .finally(() => {
          this.release(keepalive, bytes);
        });
    } catch (err: unknown) {
      // A `fetch` that throws synchronously (an absent or shimmed global)
      // never settles, so nothing else would ever hand the reservation back.
      this.release(keepalive, bytes);
      logDevError("WorkerLogBackend", "fetch", err);
    }
  }

  /**
   * Claim `bytes` of keepalive quota, or decline.
   *
   * @returns whether this request may be sent with `keepalive: true`
   */
  private reserve(bytes: number): boolean {
    if (bytes > MAX_KEEPALIVE_EVENT_BYTES) {
      logDevError(
        "WorkerLogBackend",
        "keepalive-budget",
        `event body is ${String(bytes)} bytes, over the ${String(
          MAX_KEEPALIVE_EVENT_BYTES,
        )}-byte per-event keepalive budget; sending without keepalive`,
      );
      return false;
    }
    if (this.inFlightKeepaliveBytes + bytes > KEEPALIVE_QUOTA_BYTES) {
      logDevError(
        "WorkerLogBackend",
        "keepalive-budget",
        `${String(this.inFlightKeepaliveBytes)} bytes already in flight; ` +
          `sending this ${String(bytes)}-byte event without keepalive rather ` +
          `than past the ${String(KEEPALIVE_QUOTA_BYTES)}-byte quota`,
      );
      return false;
    }
    this.inFlightKeepaliveBytes += bytes;
    return true;
  }

  /**
   * Hand a reservation back once its request settles.
   *
   * Slightly early, and knowingly so: `fetch` resolves when the response's
   * headers arrive, while the spec frees the request's keepalive bytes only
   * once the response body finishes. The ingest route answers 204 with no
   * body, so the window is a round trip's tail — and it errs in the same
   * direction as the rest of this accounting, which is why the per-event
   * budget rather than the running total is what actually holds the line.
   */
  private release(keepalive: boolean, bytes: number): void {
    if (!keepalive) return;
    this.inFlightKeepaliveBytes -= bytes;
  }

  /**
   * No-op: this backend has no buffer to drain (no client-side batching).
   * Present so callers that flush every configured backend (e.g. on
   * `visibilitychange`) don't need to special-case this one.
   */
  async flush(): Promise<void> {
    // Nothing buffered — every write() already sent its own request.
  }
}
